import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, Bell, Search, LogOut, X } from 'lucide-react';
import { clsx } from 'clsx';
import {
  LayoutDashboard,
  Users,
  Ticket,
  MapPin,
  Bus,
  BarChart3,
  Settings,
  Mail,
  CalendarDays,
  Wallet,
  Tag,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useAuth } from '../../contexts/AuthContext';

interface HeaderProps {
  onMenuToggle: () => void;
}

const navigation = [
  { name: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
  { name: 'Users', href: '/admin/dashboard/users', icon: Users },
  { name: 'Bookings', href: '/admin/dashboard/bookings', icon: Ticket },
  { name: 'Routes', href: '/admin/dashboard/routes', icon: MapPin },
  { name: 'Buses', href: '/admin/dashboard/buses', icon: Bus },
  { name: 'Schedule', href: '/admin/dashboard/schedule', icon: CalendarDays },
  { name: 'Finance', href: '/admin/dashboard/finance', icon: Wallet },
  { name: 'Promotions', href: '/admin/dashboard/promotions', icon: Tag },
  { name: 'Analytics', href: '/admin/dashboard/analytics', icon: BarChart3 },
];

const userNavigation = [
  { name: 'Emails', href: '/admin/dashboard/emails', icon: Mail },
  { name: 'Settings', href: '/admin/dashboard/settings', icon: Settings },
];

export const Header: React.FC<HeaderProps> = ({ onMenuToggle: _ }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const searchRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const location = useLocation();
  const { user, signOut } = useAuth();

  const handleSignOut = async () => {
    await signOut();
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isSearchOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isSearchOpen]);

  return (
    <>
      <header className="bg-white border-b border-secondary-200 sticky top-0 z-50 w-full">
        {/* Main header */}
        <div className="px-4 py-3 lg:px-6 w-full">
          <div className="flex items-center justify-between gap-4">
            {/* Logo and title */}
            <div className="flex items-center flex-shrink-0">
              <Link to="/admin/dashboard" className="flex items-center">
                <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">W</span>
                </div>
                <span className="ml-2 text-lg font-semibold text-secondary-900 hidden sm:block whitespace-nowrap">
                  Wote InterCity
                </span>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1 overflow-x-auto no-scrollbar py-1">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={clsx(
                      'flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap',
                      isActive
                        ? 'bg-primary-50 text-primary-700 font-bold'
                        : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
                    )}
                  >
                    <item.icon className="mr-2 h-4 w-4" />
                    {item.name}
                  </Link>
                );
              })}
            </nav>

            {/* Right side actions */}
            <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
              {/* Search */}
              <div ref={searchRef} className="hidden xl:flex items-center justify-end transition-all duration-300 ease-in-out relative">
                <div className={clsx(
                  "overflow-hidden transition-all duration-300 ease-in-out flex items-center bg-secondary-50 rounded-lg",
                  isSearchOpen ? "w-64 opacity-100" : "w-0 opacity-0"
                )}>
                  <Input
                    ref={inputRef}
                    placeholder="Search..."
                    className="w-full border-none bg-transparent focus:ring-0 px-3 py-2"
                  />
                </div>

                <Button
                  variant="ghost"
                  size="sm"
                  className={clsx("p-2 transition-transform duration-300", isSearchOpen && "absolute right-0 opacity-0 pointer-events-none")}
                  onClick={() => setIsSearchOpen(true)}
                >
                  <Search className="h-5 w-5 text-secondary-500" />
                </Button>
              </div>

              {/* Notifications */}
              <Button
                variant="ghost"
                size="sm"
                className="relative p-2"
              >
                <Bell className="h-5 w-5" />
                <span className="absolute top-1.5 right-1.5 h-2 w-2 bg-danger-500 rounded-full"></span>
              </Button>

              {/* User menu */}
              <div className="hidden lg:flex items-center relative" ref={profileRef}>
                <button
                  onClick={() => setIsProfileOpen(!isProfileOpen)}
                  className="flex items-center space-x-2 border-l pl-4 border-secondary-100 focus:outline-none"
                >
                  <div className="flex items-center rounded-full hover:bg-secondary-50 p-1 transition-colors">
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 font-medium text-sm">
                        {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                      </span>
                    </div>
                    <div className="ml-2 text-left hidden xl:block">
                      <p className="text-xs font-bold text-secondary-900 leading-tight">
                        {user?.firstName}
                      </p>
                      <p className="text-[10px] text-secondary-500 capitalize leading-tight">
                        {user?.role ? user.role.replace('_', ' ') : 'User'}
                      </p>
                    </div>
                  </div>
                </button>

                {/* Desktop Dropdown */}
                {isProfileOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-lg border border-secondary-100 py-2 z-50 transform opacity-100 scale-100 transition-all origin-top-right">
                    <div className="px-4 py-3 border-b border-secondary-100 mb-1 xl:hidden">
                      <p className="text-sm font-bold text-secondary-900 leading-tight">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p className="text-xs text-secondary-500 capitalize leading-tight">
                        {user?.role ? user.role.replace('_', ' ') : 'User'}
                      </p>
                    </div>

                    {userNavigation.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="flex items-center px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50 hover:text-primary-600 transition-colors"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        <item.icon className="mr-3 h-4 w-4" />
                        {item.name}
                      </Link>
                    ))}

                    <div className="border-t border-secondary-100 mt-1 pt-1">
                      <button
                        onClick={handleSignOut}
                        className="flex w-full items-center px-4 py-2 text-sm text-danger-600 hover:bg-danger-50 transition-colors"
                      >
                        <LogOut className="mr-3 h-4 w-4" />
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-secondary-200 bg-white shadow-xl max-h-[calc(100vh-64px)] overflow-y-auto">
            <div className="px-4 py-4">
              <nav className="space-y-1">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={clsx(
                        'flex items-center px-4 py-3 text-base font-medium rounded-xl transition-colors',
                        isActive
                          ? 'bg-primary-50 text-primary-700 font-bold'
                          : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
                      )}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <item.icon className="mr-3 h-5 w-5" />
                      {item.name}
                    </Link>
                  );
                })}

                {/* Mobile User Navigation links folded in nav */}
                <div className="my-2 border-t border-secondary-100"></div>
                {userNavigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={clsx(
                        'flex items-center px-4 py-3 text-base font-medium rounded-xl transition-colors',
                        isActive
                          ? 'bg-primary-50 text-primary-700 font-bold'
                          : 'text-secondary-600 hover:bg-secondary-50 hover:text-secondary-900'
                      )}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <item.icon className="mr-3 h-5 w-5" />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>

              {/* Mobile user info and sign out */}
              <div className="mt-6 pt-6 border-t border-secondary-200">
                <div className="flex items-center px-4 mb-6">
                  <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                    <span className="text-primary-600 font-bold">
                      {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
                    </span>
                  </div>
                  <div className="ml-4">
                    <p className="text-base font-bold text-secondary-900">
                      {user?.firstName} {user?.lastName}
                    </p>
                    <p className="text-sm text-secondary-500 capitalize">
                      {user?.role ? user.role.replace('_', ' ') : 'User'}
                    </p>
                  </div>
                </div>
                <Button
                  onClick={handleSignOut}
                  className="flex items-center justify-center w-full py-4 text-danger-600 bg-danger-50 hover:bg-danger-100 rounded-xl transition-colors font-bold"
                  variant="ghost"
                >
                  <LogOut className="mr-3 h-5 w-5" />
                  Sign out
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
};
